import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "../components/Header";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { usePageTitle } from "../lib/usePageTitle";
import { toast } from "sonner";
import {
  Shield, Users, Search as SearchIcon, ChevronRight, Ban, UserCheck, Crown, Trash2, KeyRound, RefreshCw, X, BarChart3,
  Megaphone, Upload, Mail, LogOut as LogOutIcon, Timer, Send, Inbox, Sparkles, MailPlus, Globe,
} from "lucide-react";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

function Tile({ label, value, accent }) {
  return (
    <div className="p-4 rounded-xl border border-[var(--omni-border)] bg-white">
      <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
      <div className={`text-3xl font-black tracking-tight mt-1 ${accent || ""}`}>{value}</div>
    </div>
  );
}

export default function AdminPage() {
  usePageTitle("Admin");
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [ads, setAds] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tab, setTab] = useState("users");

  useEffect(() => {
    if (loading) return;
    if (!user || user === false) { navigate("/login", { state: { from: "/admin" } }); return; }
    if (user.role !== "admin") { navigate("/", { replace: true }); return; }
    refresh();
  }, [user, loading, navigate]);

  const refresh = async () => {
    const [s, u] = await Promise.all([
      api.get("/admin/stats").then((r) => r.data),
      api.get("/admin/users").then((r) => r.data),
    ]);
    setStats(s);
    setUsers(u.users || []);
  };

  const searchUsers = async () => {
    const { data } = await api.get("/admin/users", { params: { q } });
    setUsers(data.users || []);
  };

  const openUser = async (u) => {
    setSelected(u);
    setDetail(null); setHistory([]);
    const [d, h] = await Promise.all([
      api.get(`/admin/users/${u.id}`).then((r) => r.data),
      api.get(`/admin/users/${u.id}/history`).then((r) => r.data),
    ]);
    setDetail(d); setHistory(h);
  };

  const patch = async (changes) => {
    if (!selected) return;
    try {
      const { data } = await api.patch(`/admin/users/${selected.id}`, changes);
      toast.success("Compte mis à jour");
      setSelected(data);
      setUsers((arr) => arr.map((x) => x.id === data.id ? data : x));
      const fresh = await api.get(`/admin/users/${selected.id}`);
      setDetail(fresh.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec");
    }
  };

  const resetPassword = async () => {
    if (!selected) return;
    const np = prompt("Nouveau mot de passe (min 6 caractères) :");
    if (!np || np.length < 6) return toast.error("6 caractères minimum");
    try {
      await api.post(`/admin/users/${selected.id}/reset-password`, { new_password: np });
      toast.success("Mot de passe réinitialisé");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec");
    }
  };

  const deleteUser = async () => {
    if (!selected) return;
    if (!window.confirm(`Supprimer définitivement ${selected.email} ?\nToutes ses données seront perdues.`)) return;
    try {
      await api.delete(`/admin/users/${selected.id}`);
      toast.success("Compte supprimé");
      setSelected(null); setDetail(null);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec");
    }
  };

  const loadGlobalHistory = async () => {
    const { data } = await api.get("/admin/history", { params: { limit: 300 } });
    setHistory(data);
  };

  const loadAnalytics = async () => {
    const { data } = await api.get("/admin/analytics", { params: { days: 7 } });
    setAnalytics(data);
  };

  const loadAds = async () => {
    try {
      const { data } = await api.get("/admin/ads");
      setAds(data || []);
    } catch { setAds([]); }
  };

  const loadTickets = async () => {
    try {
      const { data } = await api.get("/admin/contacts");
      setTickets(data.tickets || []);
    } catch { setTickets([]); }
  };

  const loadTemplates = async () => {
    try {
      const { data } = await api.get("/admin/email/templates");
      setTemplates(data.templates || []);
    } catch { setTemplates([]); }
  };

  if (loading || !user || user === false || user.role !== "admin") return null;

  return (
    <div className="min-h-screen flex flex-col" data-testid="admin-page">
      <Header compact />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-zinc-900 to-zinc-700 text-white flex items-center justify-center">
            <Shield size={22} />
          </div>
          <div>
            <h1 className="text-3xl font-black tracking-tight">Panel Admin</h1>
            <p className="text-xs text-gray-500">Contrôle total sur la plateforme · {user.email}</p>
          </div>
          <div className="ml-auto">
            <Button onClick={refresh} variant="outline" size="sm" className="rounded-full" data-testid="admin-refresh">
              <RefreshCw size={14} className="mr-1" /> Actualiser
            </Button>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-8" data-testid="admin-stats">
            <Tile label="Utilisateurs" value={stats.users_total} />
            <Tile label="Admins" value={stats.users_admin} accent="text-violet-600" />
            <Tile label="Bannis" value={stats.users_banned} accent="text-red-600" />
            <Tile label="Recherches" value={stats.searches} />
            <Tile label="Mails" value={stats.mails} />
            <Tile label="Drive" value={stats.drive_files} />
            <Tile label="Sessions IA" value={stats.ai_sessions_total} />
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-[var(--omni-border)] mb-4 flex gap-6 text-sm">
          <button data-testid="admin-tab-users" onClick={() => setTab("users")} className="omni-tab" data-active={tab === "users"}>
            <Users size={14} className="inline mr-1" /> Utilisateurs
          </button>
          <button data-testid="admin-tab-history" onClick={() => { setTab("history"); loadGlobalHistory(); setSelected(null); }} className="omni-tab" data-active={tab === "history"}>
            <SearchIcon size={14} className="inline mr-1" /> Historique global
          </button>
          <button data-testid="admin-tab-analytics" onClick={() => { setTab("analytics"); loadAnalytics(); setSelected(null); }} className="omni-tab" data-active={tab === "analytics"}>
            <BarChart3 size={14} className="inline mr-1" /> Analytics
          </button>
          <button data-testid="admin-tab-ads" onClick={() => { setTab("ads"); loadAds(); setSelected(null); }} className="omni-tab" data-active={tab === "ads"}>
            <Megaphone size={14} className="inline mr-1" /> Pubs
          </button>
          <button data-testid="admin-tab-tickets" onClick={() => { setTab("tickets"); loadTickets(); setSelected(null); }} className="omni-tab" data-active={tab === "tickets"}>
            <Inbox size={14} className="inline mr-1" /> Tickets
          </button>
          <button data-testid="admin-tab-emails" onClick={() => { setTab("emails"); loadTemplates(); setSelected(null); }} className="omni-tab" data-active={tab === "emails"}>
            <MailPlus size={14} className="inline mr-1" /> Emails
          </button>
        </div>

        {tab === "users" && (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-6">
            {/* User list */}
            <div className="border border-[var(--omni-border)] rounded-xl overflow-hidden">
              <div className="px-3 py-2 border-b border-[var(--omni-border)] bg-[var(--omni-surface)] flex gap-2">
                <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchUsers()} placeholder="Email, nom…" className="text-sm h-8 rounded-full bg-white" data-testid="admin-search-input" />
                <Button onClick={searchUsers} size="sm" className="bg-black hover:bg-gray-800 text-white rounded-full h-8 px-3" data-testid="admin-search-btn">Filtrer</Button>
              </div>
              <ul className="max-h-[600px] overflow-y-auto">
                {users.map((u) => (
                  <li
                    key={u.id}
                    onClick={() => openUser(u)}
                    data-testid={`admin-user-row-${u.id}`}
                    className={`px-3 py-3 border-b border-[var(--omni-border)] cursor-pointer hover:bg-[var(--omni-surface)] flex items-center gap-3 ${selected?.id === u.id ? "bg-[var(--omni-surface)]" : ""}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-[var(--omni-accent)] text-white flex items-center justify-center text-xs font-bold">
                      {(u.name || u.email)[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{u.name || u.email}</div>
                      <div className="text-xs text-gray-500 truncate">{u.email}</div>
                    </div>
                    {u.role === "admin" && <Crown size={14} className="text-amber-500" />}
                    {u.banned && <Ban size={14} className="text-red-600" />}
                    <ChevronRight size={14} className="text-gray-300" />
                  </li>
                ))}
              </ul>
            </div>

            {/* User detail */}
            <div className="border border-[var(--omni-border)] rounded-xl p-6 min-h-[600px]">
              {!selected ? (
                <div className="h-full flex items-center justify-center text-gray-400 text-sm">Sélectionnez un utilisateur</div>
              ) : (
                <div data-testid="admin-user-detail">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 rounded-full bg-[var(--omni-accent)] text-white flex items-center justify-center font-black text-xl">
                      {(selected.name || selected.email)[0].toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h2 className="text-2xl font-black tracking-tight">{selected.name || selected.email}</h2>
                        {selected.role === "admin" && <span className="text-[10px] font-mono bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">ADMIN</span>}
                        {selected.banned && <span className="text-[10px] font-mono bg-red-100 text-red-700 px-1.5 py-0.5 rounded">BANNI</span>}
                      </div>
                      <div className="text-sm text-gray-600">{selected.email}</div>
                      <div className="text-xs text-gray-400">ID : {selected.id} · Créé le {new Date(selected.created_at).toLocaleString()}</div>
                    </div>
                  </div>

                  {detail?.stats && (
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-6 text-center">
                      {[
                        ["Recherches", detail.stats.search_history],
                        ["Mails reçus", detail.stats.mails_received],
                        ["Mails envoyés", detail.stats.mails_sent],
                        ["Favoris", detail.stats.bookmarks],
                        ["Drive", detail.stats.drive_files],
                        ["Sessions IA", detail.stats.ai_sessions],
                      ].map(([l, v]) => (
                        <div key={l} className="p-2 rounded-lg bg-[var(--omni-surface)]">
                          <div className="text-lg font-black">{v}</div>
                          <div className="text-[10px] uppercase tracking-wide text-gray-500">{l}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mb-6 text-xs text-gray-500" data-testid="admin-pwd-info">
                    Les mots de passe sont chiffrés avec bcrypt et ne sont pas récupérables. Utilisez « Réinitialiser le mot de passe » ci-dessous si nécessaire.
                  </div>

                  <div className="flex flex-wrap gap-2 mb-6">
                    <Button
                      size="sm"
                      onClick={() => patch({ role: selected.role === "admin" ? "user" : "admin" })}
                      className="rounded-full bg-violet-600 hover:bg-violet-700 text-white"
                      data-testid="admin-toggle-role"
                    >
                      <Crown size={14} className="mr-1" />
                      {selected.role === "admin" ? "Retirer admin" : "Promouvoir admin"}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => patch({ banned: !selected.banned })}
                      className={`rounded-full ${selected.banned ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"} text-white`}
                      data-testid="admin-toggle-ban"
                    >
                      {selected.banned ? <><UserCheck size={14} className="mr-1" /> Débannir</> : <><Ban size={14} className="mr-1" /> Bannir</>}
                    </Button>
                    <Button size="sm" variant="outline" onClick={resetPassword} className="rounded-full" data-testid="admin-reset-pwd">
                      <KeyRound size={14} className="mr-1" /> Réinitialiser MDP
                    </Button>
                    <Button size="sm" variant="outline" onClick={deleteUser} className="rounded-full text-red-600 hover:text-red-700" data-testid="admin-delete-user">
                      <Trash2 size={14} className="mr-1" /> Supprimer
                    </Button>
                  </div>

                  <div>
                    <h3 className="text-sm font-bold mb-2">Historique de recherche ({history.length})</h3>
                    <div className="border border-[var(--omni-border)] rounded-lg max-h-64 overflow-y-auto" data-testid="admin-user-history">
                      {history.length === 0 && <div className="p-4 text-xs text-gray-500">Aucun historique.</div>}
                      {history.map((h) => (
                        <div key={h.id} className="px-3 py-1.5 text-xs border-b border-[var(--omni-border)] last:border-b-0 flex items-center gap-3">
                          <span className="font-mono text-gray-400 w-12">{h.type}</span>
                          <span className="flex-1 truncate">{h.query}</span>
                          <span className="text-gray-400">{new Date(h.created_at).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "history" && (
          <div className="border border-[var(--omni-border)] rounded-xl overflow-hidden" data-testid="admin-global-history">
            <div className="px-4 py-3 border-b border-[var(--omni-border)] bg-[var(--omni-surface)] text-sm font-bold">
              Historique de recherche global ({history.length} dernières lignes)
            </div>
            <ul className="max-h-[700px] overflow-y-auto">
              {history.map((h) => (
                <li key={h.id} className="px-4 py-2 border-b border-[var(--omni-border)] last:border-b-0 text-xs flex gap-3 items-center hover:bg-[var(--omni-surface)]">
                  <span className="font-mono text-gray-400 w-12">{h.type}</span>
                  <span className="text-gray-700 w-56 truncate">{h.user_email}</span>
                  <span className="flex-1 truncate">{h.query}</span>
                  <span className="text-gray-400">{new Date(h.created_at).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {tab === "analytics" && analytics && (
          <div className="space-y-6" data-testid="admin-analytics">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="border border-[var(--omni-border)] rounded-xl p-5">
                <div className="text-sm font-bold mb-3">Recherches sur 7 jours</div>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={analytics.searches_series}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="#0ea5e9" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="border border-[var(--omni-border)] rounded-xl p-5">
                <div className="text-sm font-bold mb-3">Nouveaux utilisateurs sur 7 jours</div>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={analytics.users_series}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#7c3aed" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="border border-[var(--omni-border)] rounded-xl p-5">
                <div className="text-sm font-bold mb-3">Répartition par type de recherche</div>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={analytics.type_breakdown} dataKey="count" nameKey="type" outerRadius={80} label>
                      {analytics.type_breakdown.map((_, i) => (
                        <Cell key={i} fill={["#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#7c3aed"][i % 5]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="border border-[var(--omni-border)] rounded-xl p-5">
                <div className="text-sm font-bold mb-3">Mails échangés sur 7 jours</div>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={analytics.mails_series}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="border border-[var(--omni-border)] rounded-xl p-5" data-testid="admin-analytics-top-queries">
                <div className="text-sm font-bold mb-3">Top 10 des requêtes</div>
                <ul className="space-y-1">
                  {analytics.top_queries.length === 0 && <li className="text-xs text-gray-400">Aucune donnée</li>}
                  {analytics.top_queries.map((q, i) => (
                    <li key={q.query + i} className="flex items-center gap-2 text-sm">
                      <span className="text-xs text-gray-400 w-6">#{i + 1}</span>
                      <span className="flex-1 truncate">{q.query}</span>
                      <span className="text-xs font-mono bg-[var(--omni-surface)] px-2 py-0.5 rounded">{q.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="border border-[var(--omni-border)] rounded-xl p-5" data-testid="admin-analytics-active-users">
                <div className="text-sm font-bold mb-3">Utilisateurs les plus actifs</div>
                <ul className="space-y-1">
                  {analytics.most_active_users.length === 0 && <li className="text-xs text-gray-400">Aucune donnée</li>}
                  {analytics.most_active_users.map((u, i) => (
                    <li key={u.email + i} className="flex items-center gap-2 text-sm">
                      <span className="text-xs text-gray-400 w-6">#{i + 1}</span>
                      <span className="flex-1 truncate">{u.email}</span>
                      <span className="text-xs font-mono bg-[var(--omni-surface)] px-2 py-0.5 rounded">{u.count} req</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {tab === "ads" && (
          <AdsPanel ads={ads} reload={loadAds} />
        )}

        {tab === "tickets" && (
          <TicketsPanel tickets={tickets} reload={loadTickets} />
        )}

        {tab === "emails" && (
          <EmailsPanel users={users} templates={templates} />
        )}
      </main>
    </div>
  );
}

function AdsPanel({ ads, reload }) {
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [triggers, setTriggers] = useState(["mail_sent"]);
  const [skipAfter, setSkipAfter] = useState(5);
  const [uploading, setUploading] = useState(false);

  const toggleTrigger = (t) => {
    setTriggers((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);
  };

  const fileToB64 = (f) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1]);
    r.onerror = reject;
    r.readAsDataURL(f);
  });

  const upload = async () => {
    if (!file) { toast.error("Choisissez une vidéo"); return; }
    if (!name.trim()) { toast.error("Nommez la pub"); return; }
    if (triggers.length === 0) { toast.error("Au moins un déclencheur"); return; }
    if (file.size > 12 * 1024 * 1024) { toast.error("Vidéo trop volumineuse (max 12 Mo)"); return; }
    setUploading(true);
    try {
      const b64 = await fileToB64(file);
      await api.post("/admin/ads", {
        name: name.trim(),
        mime: file.type || "video/mp4",
        content_b64: b64,
        triggers,
        skip_after_seconds: parseInt(skipAfter, 10) || 5,
        active: true,
      });
      toast.success("Pub ajoutée !");
      setName(""); setFile(null); setTriggers(["mail_sent"]);
      reload();
    } catch (e) {
      toast.error("Échec de l'upload : " + (e.response?.data?.detail || e.message));
    } finally { setUploading(false); }
  };

  const toggleActive = async (id, active) => {
    await api.patch(`/admin/ads/${id}`, null, { params: { active } });
    reload();
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette pub ?")) return;
    await api.delete(`/admin/ads/${id}`);
    toast.success("Supprimée");
    reload();
  };

  return (
    <div className="space-y-6" data-testid="admin-ads-panel">
      <div className="border border-[var(--omni-border)] rounded-xl p-5 bg-amber-50/30">
        <h2 className="font-bold mb-1 flex items-center gap-2"><Megaphone size={16} /> Ajouter une publicité vidéo</h2>
        <p className="text-xs text-gray-500 mb-4">Téléversez une vidéo depuis votre appareil. Elle sera diffusée aux utilisateurs selon les déclencheurs choisis.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-500">Nom (interne)</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Pub été 2026" data-testid="admin-ad-name" />
          </div>
          <div>
            <label className="text-xs text-gray-500">Vidéo (mp4 / webm, max 12 Mo)</label>
            <input
              type="file"
              accept="video/*"
              onChange={(e) => setFile(e.target.files[0] || null)}
              className="w-full text-xs file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-black file:text-white"
              data-testid="admin-ad-file"
            />
          </div>
        </div>
        <div className="mt-3">
          <label className="text-xs text-gray-500 block mb-1">Déclencheurs</label>
          <div className="flex flex-wrap gap-2">
            {[
              { id: "mail_sent", label: "Après envoi de mail", icon: Mail },
              { id: "page_leave", label: "À la sortie du site", icon: LogOutIcon },
              { id: "every_minute", label: "Toutes les minutes", icon: Timer },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => toggleTrigger(id)}
                data-testid={`admin-ad-trigger-${id}`}
                className={`text-xs px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 transition ${triggers.includes(id) ? "bg-black text-white border-black" : "border-[var(--omni-border)] hover:bg-[var(--omni-surface)]"}`}
              >
                <Icon size={12} /> {label}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-3 flex items-end gap-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Délai avant skip (s)</label>
            <Input type="number" min={0} max={60} value={skipAfter} onChange={(e) => setSkipAfter(e.target.value)} className="w-24" data-testid="admin-ad-skip" />
          </div>
          <Button onClick={upload} disabled={uploading} className="rounded-full bg-[var(--omni-accent)] hover:bg-[var(--omni-accent-hover)] text-white" data-testid="admin-ad-upload">
            <Upload size={13} className="mr-1" /> {uploading ? "Envoi…" : "Téléverser"}
          </Button>
        </div>
      </div>

      <div className="border border-[var(--omni-border)] rounded-xl overflow-hidden" data-testid="admin-ads-list">
        <div className="px-4 py-3 border-b border-[var(--omni-border)] bg-[var(--omni-surface)] text-sm font-bold">
          Pubs en circulation ({ads.length})
        </div>
        <ul>
          {ads.length === 0 && <li className="p-6 text-sm text-gray-400 italic">Aucune pub. Téléversez la première ci-dessus.</li>}
          {ads.map((ad) => (
            <li key={ad.id} className="px-4 py-3 border-b border-[var(--omni-border)] last:border-b-0 flex items-center gap-3 hover:bg-[var(--omni-surface)]" data-testid={`admin-ad-${ad.id}`}>
              <Megaphone size={16} className="text-[var(--omni-accent)] flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm truncate">{ad.name}</div>
                <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap mt-0.5">
                  <span>{(ad.size / 1024 / 1024).toFixed(1)} Mo</span>
                  <span>· skip {ad.skip_after_seconds}s</span>
                  <span>· {ad.triggers.map((t) => ({ mail_sent: "📧", page_leave: "🚪", every_minute: "⏱" }[t] || t)).join(" ")}</span>
                  <span className={ad.active ? "text-emerald-600" : "text-red-500"}>{ad.active ? "● actif" : "○ inactif"}</span>
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => toggleActive(ad.id, !ad.active)} className="rounded-full" data-testid={`admin-ad-toggle-${ad.id}`}>
                {ad.active ? "Désactiver" : "Activer"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => remove(ad.id)} className="text-red-600" data-testid={`admin-ad-delete-${ad.id}`}>
                <Trash2 size={14} />
              </Button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}


/* ------------------------ Tickets (support inbox) ------------------------ */
function TicketsPanel({ tickets, reload }) {
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const open = async (t) => {
    setSelected(t);
    setReply(t.admin_reply || t.ai_reply || "");
  };

  const sendReply = async () => {
    if (!selected) return;
    if (!reply.trim()) { toast.error("Réponse vide"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/admin/contacts/${selected.id}/reply`, {
        body: reply,
        subject: `Re: ${selected.subject}`,
      });
      toast.success(`Email envoyé à ${data.email}`);
      setSelected(data);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'envoi SMTP");
    } finally { setSending(false); }
  };

  const useAiDraft = () => {
    if (!selected?.ai_reply) return;
    setReply(selected.ai_reply);
    toast.success("Brouillon IA chargé");
  };

  const badge = (status) => {
    const m = {
      pending: ["bg-amber-100 text-amber-800", "À traiter"],
      auto_replied: ["bg-emerald-100 text-emerald-800", "Auto IA"],
      replied: ["bg-blue-100 text-blue-800", "Répondu"],
    };
    const [cls, label] = m[status] || ["bg-gray-100 text-gray-700", status];
    return <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${cls}`}>{label}</span>;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-6" data-testid="admin-tickets-panel">
      <div className="border border-[var(--omni-border)] rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-[var(--omni-border)] bg-[var(--omni-surface)] text-sm font-bold flex justify-between items-center">
          <span><Inbox size={14} className="inline mr-1" /> Tickets de support ({tickets.length})</span>
          <Button size="sm" variant="ghost" onClick={reload} className="h-7"><RefreshCw size={12} /></Button>
        </div>
        <ul className="max-h-[700px] overflow-y-auto">
          {tickets.length === 0 && <li className="p-6 text-sm text-gray-400 italic">Aucun ticket pour l'instant.</li>}
          {tickets.map((t) => (
            <li
              key={t.id}
              onClick={() => open(t)}
              className={`px-3 py-3 border-b border-[var(--omni-border)] cursor-pointer hover:bg-[var(--omni-surface)] ${selected?.id === t.id ? "bg-[var(--omni-surface)]" : ""}`}
              data-testid={`admin-ticket-row-${t.id}`}
            >
              <div className="flex items-center gap-2 mb-1">
                {badge(t.status)}
                <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${t.ai_complexity === "simple" ? "bg-violet-100 text-violet-700" : "bg-orange-100 text-orange-700"}`}>
                  {t.ai_complexity === "simple" ? "simple" : "complexe"}
                </span>
                <span className="ml-auto text-[10px] text-gray-400">{new Date(t.created_at).toLocaleString()}</span>
              </div>
              <div className="text-sm font-medium truncate">{t.subject}</div>
              <div className="text-xs text-gray-500 truncate">{t.name} · {t.email}</div>
            </li>
          ))}
        </ul>
      </div>

      <div className="border border-[var(--omni-border)] rounded-xl p-6 min-h-[600px]">
        {!selected ? (
          <div className="h-full flex items-center justify-center text-gray-400 text-sm">Sélectionnez un ticket</div>
        ) : (
          <div data-testid="admin-ticket-detail">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-[var(--omni-accent)] text-white flex items-center justify-center font-black">
                {(selected.name || "?")[0].toUpperCase()}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-xl font-black tracking-tight">{selected.subject}</h2>
                  {badge(selected.status)}
                </div>
                <div className="text-sm text-gray-600">{selected.name} &lt;{selected.email}&gt;</div>
                <div className="text-xs text-gray-400">Reçu le {new Date(selected.created_at).toLocaleString()}{selected.replied_at && ` · Répondu le ${new Date(selected.replied_at).toLocaleString()}`}</div>
              </div>
            </div>

            <div className="mb-5">
              <div className="text-xs font-bold text-gray-500 uppercase mb-2">Message</div>
              <div className="text-sm whitespace-pre-wrap p-4 rounded-lg bg-[var(--omni-surface)] border border-[var(--omni-border)]">
                {selected.message}
              </div>
            </div>

            {selected.ai_reply && (
              <div className="mb-5">
                <div className="text-xs font-bold text-gray-500 uppercase mb-2 flex items-center justify-between">
                  <span><Sparkles size={11} className="inline mr-1" /> Brouillon IA</span>
                  <button onClick={useAiDraft} className="text-[var(--omni-accent)] hover:underline normal-case" data-testid="admin-ticket-use-ai">
                    Utiliser ce brouillon →
                  </button>
                </div>
                <div className="text-sm whitespace-pre-wrap p-4 rounded-lg bg-violet-50 border border-violet-200 text-violet-900">
                  {selected.ai_reply}
                </div>
              </div>
            )}

            <div>
              <div className="text-xs font-bold text-gray-500 uppercase mb-2">Votre réponse</div>
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                rows={8}
                className="w-full text-sm p-3 border border-[var(--omni-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--omni-accent)]"
                placeholder="Tapez votre réponse… L'utilisateur la recevra par email."
                data-testid="admin-ticket-reply"
              />
              <div className="mt-3 flex items-center gap-3">
                <Button
                  onClick={sendReply}
                  disabled={sending}
                  className="rounded-full bg-[var(--omni-accent)] hover:bg-[var(--omni-accent-hover)] text-white"
                  data-testid="admin-ticket-send"
                >
                  <Send size={13} className="mr-1" /> {sending ? "Envoi…" : "Envoyer par email"}
                </Button>
                <span className="text-xs text-gray-400">→ {selected.email}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------ Direct emails / broadcast ------------------------ */
function EmailsPanel({ users, templates }) {
  const [mode, setMode] = useState("single"); // single | broadcast
  const [targetId, setTargetId] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [onlyAdmins, setOnlyAdmins] = useState(false);

  const applyTemplate = (id) => {
    setTemplateId(id);
    if (!id) return;
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    const user = users.find((u) => u.id === targetId);
    const name = user?.name || user?.email?.split("@")[0] || "";
    setSubject(t.subject);
    setBody((t.body || "").replace(/{name}/g, name).replace(/{custom}/g, ""));
  };

  const sendSingle = async () => {
    if (!targetId) { toast.error("Choisissez un destinataire"); return; }
    if (!subject.trim() || !body.trim()) { toast.error("Sujet et message requis"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/admin/email/send", {
        user_id: targetId, subject, body, template: templateId || null,
      });
      toast.success(`Email envoyé à ${data.to}`);
      setSubject(""); setBody(""); setTemplateId("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'envoi");
    } finally { setSending(false); }
  };

  const sendBroadcast = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("Sujet et message requis"); return; }
    const count = users.filter((u) => !u.banned && (!onlyAdmins || u.role === "admin")).length;
    if (!window.confirm(`Envoyer ce mail à ${count} utilisateur(s) ?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/email/broadcast", {
        subject, body, only_admins: onlyAdmins,
      });
      toast.success(`Diffusé : ${data.sent}/${data.recipients} (${data.failed} échec(s))`);
      setSubject(""); setBody("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de la diffusion");
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-emails-panel">
      <div className="flex gap-2">
        <button
          onClick={() => setMode("single")}
          className={`px-4 py-2 rounded-full text-sm transition ${mode === "single" ? "bg-black text-white" : "border border-[var(--omni-border)] hover:bg-[var(--omni-surface)]"}`}
          data-testid="admin-email-mode-single"
        >
          <Mail size={13} className="inline mr-1" /> À un utilisateur
        </button>
        <button
          onClick={() => setMode("broadcast")}
          className={`px-4 py-2 rounded-full text-sm transition ${mode === "broadcast" ? "bg-black text-white" : "border border-[var(--omni-border)] hover:bg-[var(--omni-surface)]"}`}
          data-testid="admin-email-mode-broadcast"
        >
          <Globe size={13} className="inline mr-1" /> Annonce à tous
        </button>
      </div>

      <div className="border border-[var(--omni-border)] rounded-xl p-5 space-y-4">
        {mode === "single" && (
          <div>
            <label className="text-xs text-gray-500 block mb-1">Destinataire</label>
            <select
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              className="w-full p-2 border border-[var(--omni-border)] rounded-lg text-sm bg-white"
              data-testid="admin-email-target"
            >
              <option value="">— Choisir un utilisateur —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name ? `${u.name} (${u.email})` : u.email}{u.role === "admin" ? " · admin" : ""}{u.banned ? " · banni" : ""}
                </option>
              ))}
            </select>
          </div>
        )}

        {mode === "broadcast" && (
          <div className="flex items-center gap-2 text-sm">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={onlyAdmins}
                onChange={(e) => setOnlyAdmins(e.target.checked)}
                data-testid="admin-email-only-admins"
              />
              Envoyer uniquement aux admins
            </label>
            <span className="ml-auto text-xs text-gray-500">
              Cible actuelle : <strong>{users.filter((u) => !u.banned && (!onlyAdmins || u.role === "admin")).length}</strong> utilisateur(s)
            </span>
          </div>
        )}

        <div>
          <label className="text-xs text-gray-500 block mb-1">Template</label>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => { setTemplateId(""); setSubject(""); setBody(""); }}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${templateId === "" ? "bg-black text-white border-black" : "border-[var(--omni-border)] hover:bg-[var(--omni-surface)]"}`}
              data-testid="admin-email-template-blank"
            >
              Champ libre
            </button>
            {templates.map((t) => (
              <button
                key={t.id}
                onClick={() => applyTemplate(t.id)}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${templateId === t.id ? "bg-black text-white border-black" : "border-[var(--omni-border)] hover:bg-[var(--omni-surface)]"}`}
                data-testid={`admin-email-template-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs text-gray-500 block mb-1">Sujet</label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Sujet de l'email" data-testid="admin-email-subject" />
        </div>

        <div>
          <label className="text-xs text-gray-500 block mb-1">Message</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={10}
            className="w-full text-sm p-3 border border-[var(--omni-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--omni-accent)]"
            placeholder="Tapez votre message… ({name} sera remplacé automatiquement)"
            data-testid="admin-email-body"
          />
        </div>

        <div className="flex items-center justify-end gap-3">
          <Button
            onClick={mode === "single" ? sendSingle : sendBroadcast}
            disabled={sending}
            className="rounded-full bg-[var(--omni-accent)] hover:bg-[var(--omni-accent-hover)] text-white"
            data-testid={mode === "single" ? "admin-email-send-single" : "admin-email-send-broadcast"}
          >
            <Send size={13} className="mr-1" />
            {sending ? "Envoi…" : (mode === "single" ? "Envoyer l'email" : "Diffuser à tous")}
          </Button>
        </div>
      </div>
    </div>
  );
}
